import { DeleteOutlined, EditTwoTone, UsergroupAddOutlined } from "@ant-design/icons";
import { capitalizeFirstLetter } from "@src/appV2/lib/utils/capitalizeFirstLetter";
import { Button, Card, Collapse, Input, List, Modal, Rate, Select, Skeleton, Switch } from "antd";
import { Drawer } from "@src/appV2/redesign/components/Drawer";
import { ModalStatus, type UseModalState } from "@src/lib/ui-react";
import _ from "lodash";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import request from "superagent";
import DisabledInputPopover from "../../components/disabledInputPopover";
import { Edit } from "../../components/edit";
import { FacilityAppLinksCard } from "../../components/facilityAppLinksCard";
import { FacilityNotes } from "../../components/FacilityNotes/FacilityNotes";
import { ProfileChangeLogsCard } from "../../components/profileChangeLogs";
import SelectLocations from "../../components/selectLocations";
import SelectMetropolitanStatisticalArea from "../../components/SelectMetropolitanStatisticalArea";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import {
  FACILITY_PREFERENCE_BASE_URL,
  HOLIDAY_DIFFERENTIAL,
  RATES_TABLE,
} from "../../constants/facility";
import { ADMIN_EVENTS } from "../../constants/analyticsEvents";
import { FacilityStatus, FacilityStatusObj } from "@src/appV2/redesign/utils/facility";
import meta from "../../utils/meta";
import { logEvent } from "@src/appV2/lib/analytics";
import { getAuthHeader } from "src/utils/superagent";
import { FacilityBilling } from "src/containers/facilityDrawer/billing";
import { ReferenceTimeSheet } from "src/containers/facilityDrawer/referenceTimeCard";
import { PreferenceHeading } from "src/containers/shiftVerificationPreferences/components/heading";
import { HcfRequirement } from "src/containers/userRequirements";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { AutoLoginLinksModal } from "./AutoLoginLinksModal";
import { FacilityReviewCard } from "./facilities/facilityReviewCard";
import { Log, StatusChangeLogsCard } from "../../components/StatusChangeLogsCard";
import { FacilityPhotos } from "./facilityPhotos";
import { FacilityStatusModal } from "./facilityStatusModal";
import { FPNCard } from "./FPNCard";
import { HolidayRateModal } from "./HolidayRateModal";
import { InstantPayDrawerCard } from "./instantPayDrawerCard";
import { LocationContainer } from "./locationContainer";
import { LogContainer } from "./logContainer";
import { RateNegotiationStatusCard } from "./rateNegotiationStatusCard/RateNegotiationStatusCard";
import moment from "moment-timezone";
import { LDFlagSet, withLDConsumer } from "launchdarkly-react-client-sdk";
import { updateFacilityProfile } from "../../api/facility";
import { handleApiError } from "src/utils/errors";
import { CustomSettings } from "../workplaceSettings";
import { TeamMembers } from "src/containers/teamMembers";
import { User } from "src/api/user";
import { Facility, Holiday } from "src/interface";
import { Session } from "src/modules/interface";
import { LocationConfig } from "src/types/location";
import { VariableChargeToggle } from "../facilityDrawer/variableCharge/variableChargeToggle";
import { FacilityInvalidContractWarning } from "@src/appV2/FacilityDrawer/BillingAlerts/FacilityInvalidContractWarning";
import { FacilityPendingNotice } from "@src/appV2/FacilityDrawer/BillingAlerts/FacilityPendingNotice";
import { DEFAULT_TIMEZONE } from "@src/constants/timezone";
import { pseudo } from "@src/modules/session";
import { bindActionCreators, type Dispatch } from "redux";
import { Box, Stack } from "@mui/material";
import { fetchFacility } from "../requirements/mappingsTab/api";
import { CorporateFacilities } from "./CorporateFacilities";
import { useGetWorkplaceTypes } from "@src/appV2/Admin/WorkplaceTypes/api/useGetWorkplaceTypes";
import AccountManagerDisplay from "../../components/AccountManagerDisplay";
import BillingSpecialistDisplay from "../../components/BillingSpecialistDisplay";
import { CopyableText } from "@src/appV2/CopyableText";
import { isDefined } from "@clipboard-health/util-ts";
import { ShiftBoardLinkButton } from "@src/appV2/ShiftBoard/components/ShiftBoardLinkButton";
import { WorkplaceAgreementSection } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementSection";
import { QualificationHolidayFeeOverridesContainer } from "@src/appV2/FacilityDrawer/QualificationHolidayFeeOverrides/QualificationHolidayFeeOverrides";
import { Ten99PolicyCoverageCard } from "@src/appV2/Ten99PolicyCoverage/Ten99PolicyCoverageCard";
import { ShiftMigrationDialog } from "@src/appV2/redesign/ShiftMigration/components/ShiftMigrationDialog";

const { Option } = Select;
const { Panel } = Collapse;
const ButtonGroup = Button.Group;
const { confirm } = Modal;

const FACILITY_DRAWER_SECTIONS = {
  LOCATION_ADDRESS: "location-address",
  ACCOUNT_MANAGERS: "account-managers",
  SETTINGS_VERIFICATION: "settings-verification",
  DOCUMENTS: "documents",
  WORKPLACE_INFORMATION: "workplace-information",
  BILLING_PAYMENTS: "billing-payments",
  CHANGE_OF_OWNERSHIP: "change-of-ownership",
  LOGS_HISTORY: "logs-history",
} as const;

const ALL_SECTION_KEYS = Object.values(FACILITY_DRAWER_SECTIONS);

interface WorkplaceTypeSelectProps {
  style?: React.CSSProperties;
  placeholder: string;
  defaultValue?: string;
  onChange: (value: string) => void;
}

function WorkplaceTypeSelect(props: WorkplaceTypeSelectProps) {
  const { style, placeholder, defaultValue, onChange } = props;
  const { data, isLoading } = useGetWorkplaceTypes();
  const workplaceTypes = data?.data ?? [];

  return (
    <Select
      style={style}
      placeholder={placeholder}
      onChange={(value) => onChange(value)}
      defaultValue={defaultValue}
      loading={isLoading}
    >
      {workplaceTypes.map(({ attributes }) => (
        <Option value={attributes.name} key={attributes.name}>
          {attributes.displayLabel}
        </Option>
      ))}
    </Select>
  );
}

function ImpersonateButton({ onClick }: { onClick: () => void }) {
  const isImpersonationEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_COGNITO_IMPERSONATION, {
    defaultValue: false,
  });

  return (
    <Button type="primary" onClick={onClick}>
      {isImpersonationEnabled ? "Impersonate User" : "Auto Login Links"}
    </Button>
  );
}

interface FacilityDrawerProps {
  userId?: string;
  closeDrawer: (value: string) => void;
  isAdmin?: boolean;
  ldFlags: LDFlagSet | undefined;
  currentUserID?: string;
  session?: Session;
  workPlaceTypes?: Array<{ id: string; name: string; type: string }>;
  locations?: LocationConfig;
  pseudo: (data: { userId: string }) => (dispatch: Dispatch) => void;
}

interface FacilityDrawerState {
  payrollVisible: boolean;
  coordinatorsVisible: boolean;
  addingCoordinator: boolean;
  editingDocuments: boolean;
  updatingNotifyShift: boolean;
  facilityTypeLoading: boolean;
  updatingInstantBook: boolean;
  updatingPayOnHolidayCheck: boolean;
  updatingPayOnAllHolidaysCheck: boolean;
  updatingHolidayList: boolean;
  user?: Omit<User, "facility"> & { facility: Facility };
  parentFacility?: { userId: string; name: string; salesforceID: string };
  ratesTable?: Facility["ratesTable"];
  admins: [];
  showStatusModal: boolean;
  selectedStatus: string;
  coordinatorNotes: null;
  holidays: Holiday[];
  showUsersModal: boolean;
  ratings: Record<string, number>;
  statusLogs: Log[];
  holidayRateConfig: [];
  showAutoLoginLinksModal: boolean;
  showCoordinatorsInfo: boolean;
  showPayrollInfoModal: boolean;
  showPayrollInfo: boolean;
  instantBookStatus: boolean;
  isInstructions: boolean;
  showCallInstructions: boolean;
  callFacilityDetailsLoading: boolean;
  showHolidayRate: boolean;
  showShiftMigrationModal: boolean;
  payOnHoliday: boolean;
  salesforceIDError?: string;
  copiedPricingMsa: boolean;
  activeSectionKeys: string[];
}

export class FacilityDrawer extends React.Component<FacilityDrawerProps, FacilityDrawerState> {
  state: FacilityDrawerState = {
    payrollVisible: false,
    coordinatorsVisible: false,
    addingCoordinator: false,
    editingDocuments: false,
    updatingNotifyShift: false,
    facilityTypeLoading: false,
    updatingInstantBook: false,
    updatingPayOnHolidayCheck: false,
    updatingPayOnAllHolidaysCheck: false,
    updatingHolidayList: false,
    admins: [],
    showStatusModal: false,
    selectedStatus: "",
    coordinatorNotes: null,
    holidays: [],
    showUsersModal: false,
    ratings: {},
    statusLogs: [],
    holidayRateConfig: [],
    showAutoLoginLinksModal: false,
    showCoordinatorsInfo: false,
    showPayrollInfoModal: false,
    showPayrollInfo: false,
    instantBookStatus: false,
    isInstructions: false,
    showCallInstructions: false,
    callFacilityDetailsLoading: false,
    showHolidayRate: false,
    showShiftMigrationModal: false,
    payOnHoliday: false,
    copiedPricingMsa: false,
    activeSectionKeys: [],
  };

  // form refs
  coordinatorName = null;
  coordinatorEmail = null;
  coordinatorPhone = null;

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line react/no-deprecated
  componentWillMount() {
    void this.fetchAdmins();
    void this.fetchAllHolidays();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userId !== this.props.userId && this.props.userId) {
      void this.retrieveUser(this.props.userId);
      void this.getFacilityStatusLogs();
      logEvent(ADMIN_EVENTS.OPEN_HCF_DRAWER);
    }
    // When the drawer closes (userId cleared), reset transient view flags and
    // drop the cached user so the next open re-fetches fresh data. Guarded to
    // avoid a setState/render loop when already reset.
    if (prevProps.userId && !this.props.userId) {
      const { showCoordinatorsInfo, showPayrollInfo, showShiftMigrationModal, user } = this.state;
      if (showCoordinatorsInfo || showPayrollInfo || showShiftMigrationModal || user) {
        // eslint-disable-next-line react/no-did-update-set-state -- guarded against render loop; legacy class component
        this.setState({
          showCoordinatorsInfo: false,
          showPayrollInfo: false,
          showShiftMigrationModal: false,
          user: undefined,
          salesforceIDError: undefined,
        });
      }
    }
  }

  handleHolidayRate = (holidayRateConfig) => {
    this.setState({
      showHolidayRate: !this.state.showHolidayRate,
      holidayRateConfig,
    });
  };

  saveHolidayConfig = (holidayRateConfig) => {
    const data = {
      holidayFee: holidayRateConfig.holidayFee,
      facilityId: this.props.userId,
    };
    this.saveFacility(data)
      .then(() => {
        this.setState({
          showHolidayRate: false,
          holidayRateConfig: [],
        });
      })
      .catch(handleApiError);
  };

  saveFacility = async (data) => {
    await updateFacilityProfile({
      ...data,
      performedBy: this.props.currentUserID,
    });
    // retrieveUser refreshes local state after a successful profile update.
    // A failure here (e.g. transient network issue on the follow-up GET)
    // must not reject saveFacility — otherwise callers doing optimistic
    // update + revert-on-error would undo a write that actually succeeded.
    try {
      return await this.retrieveUser(data.facilityId);
    } catch {
      return undefined;
    }
  };

  saveUser = async (data) => {
    return request
      .put(`${global.api}/user/put`)
      .set(await getAuthHeader())
      .send({ ...data, performedBy: this.props.currentUserID })
      .then(() => {
        void this.retrieveUser(data.id);
      });
  };

  getFacilityStatusLogs = async () => {
    const { body } = await request
      .get(`${global.api}/facilityProfile/status/logs`)
      .set(await getAuthHeader())
      .query({ facilityId: this.props.userId });
    this.setState({ statusLogs: body.logs });
  };

  mergeRates = (rates) => {
    return _.merge(_.cloneDeep(RATES_TABLE), rates);
  };

  retrieveUser = async (userId?: string) => {
    const { body } = await request
      .get(`${global.api}/user/get/${userId}`)
      .set(await getAuthHeader());

    this.setState({
      user: body,
      ratesTable: this.mergeRates(body.facility.ratesTable) || undefined,
      parentFacility: undefined,
    });

    if (body.facility.parentFacilityId) {
      const parentFacility = await fetchFacility(
        body.facility.parentFacilityId,
        "userId name salesforceID"
      );
      this.setState({
        parentFacility,
      });
    }

    return body;
  };

  fetchAdmins = async () => {
    const { body } = await request.get(`${global.api}/user/admins`).set(await getAuthHeader());
    this.setState({ admins: body });
  };

  fetchAllHolidays = async () => {
    const { body } = await request.get(`${global.api}/holidays`).set(await getAuthHeader());
    const holidays = body.map((h) => ({ id: h._id, name: h.name }));
    this.setState({ holidays });
  };

  editPayroll = (payroll, key, val) => {
    payroll[key] = val;
    this.saveFacility({
      facilityId: this.props.userId,
      payroll,
    }).catch(handleApiError);
  };

  onCoordinatorNotesChange = (event) => {
    this.setState({ coordinatorNotes: event.target.value });
  };

  roundHalf(num) {
    return Math.round(num / 0.5) * 0.5;
  }

  getCoordinators(user: User) {
    const coordinators = user.facility?.coordinators || [];
    const header = (
      <span>
        <Button
          size="small"
          type="default"
          onClick={() => this.setState({ addingCoordinator: true })}
        >
          Show Coordinators
        </Button>
        &nbsp;
      </span>
    );

    return (
      <List
        size="small"
        header={header}
        bordered
        dataSource={coordinators}
        renderItem={(coordinator) => {
          if (!this.state.showCoordinatorsInfo) {
            return <Fragment />;
          }
          return (
            <List.Item>
              <Button
                shape="circle"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => {
                  this.saveFacility({
                    facilityId: user._id,
                    coordinators: coordinators.filter((c) => c.name !== coordinator.name),
                  }).catch(handleApiError);
                }}
              />
              &nbsp;
              {coordinator.name}
              <br />
              {coordinator.phone}
              <br />
              {coordinator.email}
              <br />
              <i>{coordinator.notes}</i>
            </List.Item>
          );
        }}
      />
    );
  }

  filterManager = (input, option) =>
    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  updateFacilityType = async (value) => {
    this.setState({ facilityTypeLoading: true });
    const { userId } = this.props;
    try {
      await this.saveFacility({
        facilityId: userId,
        type: value,
        description: "",
      });
    } catch (error) {
      handleApiError(error);
    } finally {
      this.setState({ facilityTypeLoading: false });
    }
  };

  updateSalesforceID = async (event) => {
    const { value: salesforceId } = event.target;

    if (!/^[0-9a-zA-Z]{15}([0-9a-zA-Z]{3})?$/.test(salesforceId)) {
      this.setState({ salesforceIDError: "Invalid salesforce id" });
      return;
    }

    const { body } = await request
      .get(`${global.api}/salesforce/${salesforceId}/relationships/workplaces`)
      .set(await getAuthHeader());
    if (body.data) {
      this.setState({ salesforceIDError: "Salesforce id already in use." });
      return;
    }

    const { userId } = this.props;
    this.setState({ salesforceIDError: undefined });
    try {
      await this.saveFacility({
        facilityId: userId,
        salesforceID: salesforceId,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  handleShiftAssignment = (checked: boolean) => {
    const { userId } = this.props;
    this.setState({ updatingNotifyShift: true });
    void this.saveFacility({
      facilityId: userId,
      notifyShiftAssignment: checked,
    })
      .catch(handleApiError)
      .finally(() => {
        this.setState({ updatingNotifyShift: false });
      });
  };

  handlePayOnHoliday = (checked: boolean) => {
    const { userId } = this.props;
    this.setState({ updatingPayOnHolidayCheck: true });
    let updateData: {
      facilityId?: string;
      payOnHoliday: boolean;
      payOnAllHolidays?: boolean;
    } = {
      facilityId: userId,
      payOnHoliday: checked,
    };
    if (checked) {
      updateData = { ...updateData, payOnAllHolidays: true };
    }
    void this.saveFacility(updateData)
      .then(() => {
        this.setState({ payOnHoliday: checked });
      })
      .catch(handleApiError)
      .finally(() => {
        this.setState({ updatingPayOnHolidayCheck: false });
      });
  };

  handlePayOnAllHolidays = (checked: boolean) => {
    const { userId } = this.props;
    this.setState({ updatingPayOnAllHolidaysCheck: true });
    void this.saveFacility({
      facilityId: userId,
      payOnAllHolidays: checked,
    })
      .catch(handleApiError)
      .finally(() => {
        this.setState({ updatingPayOnAllHolidaysCheck: false });
      });
  };

  handleHolidayListChange = (value?: string[]) => {
    const { userId } = this.props;
    this.setState({ updatingHolidayList: true });
    void this.saveFacility({
      facilityId: userId,
      holidayList: value,
    })
      .catch(handleApiError)
      .finally(() => {
        this.setState({ updatingHolidayList: false });
      });
  };

  handleDrawerClose = () => {
    const { type } = this.state.user?.facility ?? {};
    this.props.closeDrawer(type || "");
    this.setState({
      user: undefined,
      salesforceIDError: undefined,
      showShiftMigrationModal: false,
    });
  };

  saveFacilityStatus = async (requestData) => {
    return request
      .put(`${global.api}/facilityProfile/status`)
      .set(await getAuthHeader())
      .send(requestData)
      .then(() => {
        void this.retrieveUser(this.props.userId);
        void this.getFacilityStatusLogs();
        this.setState({ showStatusModal: false, selectedStatus: "" });
      });
  };

  toggleUserModal = () => this.setState({ showUsersModal: !this.state.showUsersModal });

  isRequiredToOpenStatusModal = (currentStatus, previousStatus) => {
    return (
      [
        FacilityStatusObj.PROBATION,
        FacilityStatusObj.SUSPENDED,
        FacilityStatusObj.TERMINATED,
        FacilityStatusObj.PENDING,
        FacilityStatusObj.UNSIGNED,
      ].includes(currentStatus) ||
      ([
        FacilityStatusObj.SUSPENDED,
        FacilityStatusObj.TERMINATED,
        FacilityStatusObj.UNSIGNED,
      ].includes(previousStatus) &&
        [
          FacilityStatusObj.ENROLLED,
          FacilityStatusObj.ONBOARDING,
          FacilityStatusObj.PROBATION,
        ].includes(currentStatus))
    );
  };

  openStatusModal = async (status) => {
    const { facility } = this.state.user ?? {};
    if (facility?.status === status) {
      return;
    }
    if (this.isRequiredToOpenStatusModal(status, facility?.status)) {
      this.setState({ selectedStatus: status, showStatusModal: true });
    } else {
      if (status === FacilityStatusObj.ARCHIVED) {
        confirm({
          title: "Confirm you want to make this change?",
          content:
            "Archiving this workplace will automatically cancel all future booked shifts and notify affected workers.",
          onOk: async () => {
            // update the status without opening the modal
            await this.saveFacilityStatus({
              adminId: this.props.currentUserID,
              facilityId: this.props.userId,
              status,
              prevStatus: facility?.status,
              reason: "",
              note: "",
            });
            this.setState({ selectedStatus: status });
          },
        });
      } else {
        // update the status without opening the modal
        await this.saveFacilityStatus({
          adminId: this.props.currentUserID,
          facilityId: this.props.userId,
          status,
          prevStatus: facility?.status,
          reason: "",
          note: "",
        });
        this.setState({ selectedStatus: status });
      }
    }
  };

  getRatingtext = () => {
    const ratingCount = _.get(this.state, "user.facility.rating.count", 0);
    const ratingValue = _.get(this.state, "user.facility.rating.value", 0).toFixed(1);
    if (ratingCount === 0) {
      return "no shift reviews yet";
    } else if (ratingCount === 1) {
      return `${ratingValue} average from ${ratingCount} shift rating`;
    } else {
      return `${ratingValue} average from ${ratingCount} shift ratings`;
    }
  };

  handleSectionChange = (keys: string | string[]) => {
    const activeKeys = Array.isArray(keys) ? keys : [keys];
    this.setState({ activeSectionKeys: activeKeys });
  };

  handleToggleAllSections = () => {
    const isAllExpanded = this.state.activeSectionKeys.length === ALL_SECTION_KEYS.length;
    this.setState({
      activeSectionKeys: isAllExpanded ? [] : [...ALL_SECTION_KEYS],
    });
  };

  render() {
    const { userId, currentUserID, session } = this.props;
    const { FLAT, CHARGE_RATE_MULTIPLIER } = HOLIDAY_DIFFERENTIAL;
    const {
      user,
      parentFacility,
      updatingPayOnHolidayCheck,
      updatingPayOnAllHolidaysCheck,
      updatingHolidayList,
      holidays,
    } = this.state;
    const enoughRatings =
      _.get(this.state, "user.facility.rating.value", 0) < 3 &&
      _.get(this.state, "user.facility.rating.count", 0) < 5
        ? 0
        : _.get(this.state, "user.facility.rating.value", 0);
    const ratingText = this.getRatingtext();
    const ratingSubtext = !enoughRatings &&
      _.get(this.state, "user.facility.rating.count", 0) > 0 && (
        <p style={{ color: "#9a9a9a" }}>
          (HCPs cannot see this rating until you have at least 5 reviews)
        </p>
      );
    // Adapter: the drawer's "open" signal is the `userId` prop (caller controls
    // when to pass an id vs undefined). Wrap that into the redesign Drawer's
    // modalState shape. Open/setStatus are no-ops because open is externally
    // controlled; only close propagates back via `closeDrawer`.
    const modalState: UseModalState = {
      modalIsOpen: Boolean(userId),
      modalIsClosed: !userId,
      modalStatus: userId ? ModalStatus.OPEN : ModalStatus.CLOSED,
      setModalStatus: () => undefined,
      openModal: () => undefined,
      closeModal: this.handleDrawerClose,
    };

    // Override the redesign Drawer's 400px default — the workplace details
    // panel is content-heavy, matching the legacy 60vw width.
    //
    // zIndex note: the Drawer carries a large tree of antd Modals (status
    // change, "Manage Users", Auto Login Links, location edit/view, etc.).
    // Antd Modals default to zIndex 1000, while MUI's temporary Drawer sits
    // at theme.zIndex.modal (1300) — so every nested antd modal would render
    // underneath the drawer. Rather than bump each modal individually (and
    // every modal added in the future), pin the drawer below antd's default
    // so any nested antd surface naturally stacks on top. MUI's own overlays
    // (Dialog, Snackbar, Tooltip) still sit above the drawer since their
    // defaults are >= 1200.
    const drawerSx = {
      zIndex: 999,
      "& .MuiDrawer-paper": {
        width: { xs: "100%", md: "60vw" },
        maxWidth: "100%",
      },
    };

    // Short-circuit when the drawer is closed or the workplace hasn't loaded
    // yet. Fragment root matches the loaded-state return below so React
    // reconciles the same <Drawer> element across the loading → loaded
    // transition instead of remounting it (which would replay the slide-in).
    // The combined guard also lets TS narrow `userId` to `string` below.
    if (!userId || !user) {
      return (
        <>
          <Drawer
            closeOnEscape
            modalState={modalState}
            title="Workplace Details"
            showCloseButton
            sx={drawerSx}
          >
            <Skeleton loading />
          </Drawer>
        </>
      );
    }

    const { facility, zenDeskId } = user;

    if (!facility.payroll) {
      facility.payroll = { name: "", email: "" };
    }

    if (!facility.hasOwnProperty("notifyShiftAssignment")) {
      facility.notifyShiftAssignment = true;
    }

    const twoSpace = <div style={{ height: 16 }} />;

    const { zenDeskContact } = meta();
    const isAllExpanded = this.state.activeSectionKeys.length === ALL_SECTION_KEYS.length;

    // Status change + impersonate actions — sticky at the bottom of the drawer.
    const footerControls = (
      <Stack direction="column" spacing={2} alignItems="flex-start">
        <ButtonGroup>
          {FacilityStatus.filter((status) =>
            status === FacilityStatusObj.ARCHIVED
              ? this.props.session?.adminProfile.roles?.includes("SHIFT_PRICING")
              : true
          ).map((status) => (
            <Button
              key={status}
              danger={facility.status === status}
              type={"default"}
              onClick={() => this.openStatusModal(status)}
            >
              {capitalizeFirstLetter(status)}
            </Button>
          ))}
        </ButtonGroup>
        <ImpersonateButton onClick={() => this.setState({ showAutoLoginLinksModal: true })} />
      </Stack>
    );

    return (
      <>
        <Drawer
          closeOnEscape
          modalState={modalState}
          title="Workplace Details"
          showCloseButton
          sx={drawerSx}
          footerContent={footerControls}
        >
          <div>
            {/* Test Account toggle + Zendesk link — formerly rendered in the
                antd Drawer's title bar; the redesign Drawer's title slot is
                text-only, so they live at the top of the body now. */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={2}
              sx={{ marginBottom: 2 }}
            >
              <label>{"Test Account"}</label>
              <Switch
                disabled={
                  !this.props.session?.adminProfile?.roles?.includes(EMPLOYEE_ROLES.TEST_ACCOUNTS)
                }
                defaultChecked={facility.isTestAccount}
                onChange={(checked) => {
                  this.saveFacility({
                    facilityId: user._id,
                    isTestAccount: checked,
                  }).catch(handleApiError);
                }}
              />
              {zenDeskId && (
                <Button type="link" href={`${zenDeskContact}/${zenDeskId}`} target="_blank">
                  <img style={{ width: "30px" }} src="/zenDesk.svg" alt="zendesk" />
                </Button>
              )}
            </Stack>

            {/* Basic Information - Always visible */}
            <div style={{ marginBottom: 16 }}>
              <Input
                addonBefore="User Id"
                style={{ width: "55%" }}
                addonAfter={
                  isDefined(facility.userId) ? (
                    <CopyableText value={facility.userId} showText={false} />
                  ) : null
                }
                disabled={true}
                defaultValue={facility.userId}
              />
              {twoSpace}
              <Edit
                addonBefore="Name"
                placeholder="Name"
                style={{ width: "100%" }}
                required
                defaultValue={facility.name}
                eventFn={(value) => this.saveFacility({ facilityId: user._id, name: value })}
                addonAfter={
                  isDefined(facility.name) ? (
                    <CopyableText value={facility.name} showText={false} />
                  ) : null
                }
              />
              {twoSpace}
              <Edit
                addonBefore="Email"
                placeholder="Email"
                defaultValue={user.email}
                eventFn={(value) => this.saveUser({ id: user._id, email: value })}
                addonAfter={
                  isDefined(user.email) ? (
                    <CopyableText value={user.email} showText={false} />
                  ) : null
                }
              />
              {twoSpace}
              <Edit
                addonBefore="Phone"
                placeholder="Phone"
                defaultValue={facility.phone}
                eventFn={(value) => this.saveFacility({ facilityId: user._id, phone: value })}
                addonAfter={
                  isDefined(facility.phone) ? (
                    <CopyableText value={facility.phone} showText={false} />
                  ) : null
                }
              />
              {twoSpace}
              <div>
                <label>{"Rating"}</label>&nbsp;&nbsp;
                <Rate
                  disabled
                  value={this.roundHalf(_.get(this.state, "user.facility.rating.value", 0))}
                  allowHalf
                />{" "}
                {ratingText}
                {ratingSubtext}
              </div>
              {twoSpace}
              <div>
                <label>{"Type"}</label>
                <WorkplaceTypeSelect
                  style={{ width: "100%" }}
                  placeholder="Select Workplace Type"
                  onChange={this.updateFacilityType}
                  defaultValue={facility.type}
                />
              </div>
              {twoSpace}
              {
                <FPNCard
                  saveFacility={this.saveFacility}
                  facility={facility}
                  user={this.state.user}
                />
              }
              {twoSpace}
              <div>
                <label style={{ display: "inline-block", minWidth: 180 }}>{"Salesforce ID"}</label>
                &nbsp;
                <Input
                  defaultValue={facility?.salesforceID}
                  placeholder="Salesforce ID"
                  onBlur={this.updateSalesforceID}
                />
                {this.state.salesforceIDError && (
                  <span
                    style={{ color: "red", marginLeft: 8 }}
                  >{`*${this.state.salesforceIDError}`}</span>
                )}
              </div>
              {twoSpace}
              {isDefined(facility.userId) && (
                <>
                  <CorporateFacilities
                    switchToUserSession={this.props.pseudo}
                    parentWorkplace={parentFacility}
                    currentWorkplace={{
                      userId: facility.userId,
                      name: facility.name ?? "This workplace",
                      salesforceID: facility.salesforceID,
                    }}
                  />
                  {twoSpace}
                </>
              )}
              <div>
                <Button
                  type="primary"
                  icon={<UsergroupAddOutlined />}
                  size="large"
                  onClick={this.toggleUserModal}
                >
                  Show Users
                </Button>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 8,
              }}
            >
              <Button onClick={this.handleToggleAllSections}>
                {isAllExpanded ? "Collapse All" : "Expand All"}
              </Button>
            </div>

            <Collapse
              activeKey={this.state.activeSectionKeys}
              onChange={this.handleSectionChange}
              destroyInactivePanel={false}
            >
              <Panel header="Location & Address" key={FACILITY_DRAWER_SECTIONS.LOCATION_ADDRESS}>
                <div>
                  <label>{"Location"}</label>&nbsp;&nbsp;
                  <SelectLocations
                    label="Select location"
                    multiple={false}
                    locations={this.props.locations}
                    defaultValue={facility.location}
                    onChange={(location: LocationConfig) => {
                      this.saveFacility({
                        facilityId: user._id,
                        location: location._id,
                      }).catch(handleApiError);
                    }}
                  />
                </div>
                {twoSpace}
                <LocationContainer
                  address={facility.fullAddress}
                  location={facility.geoLocation}
                  userId={user._id}
                  retrieveUser={this.retrieveUser}
                  saveUser={this.saveUser}
                  type="FACILITY"
                  admin={currentUserID}
                />
                {twoSpace}
                <DisabledInputPopover
                  placeholder={
                    user.tmz +
                    " - " +
                    moment.tz(user.tmz).format("zz") +
                    " (" +
                    moment.tz(user.tmz).format("z") +
                    ")"
                  }
                  inputName="Time Zone"
                  popover="Time zones are automatically assigned based on the Address. Edit the Address to re-calculate the time zone."
                />
                {twoSpace}
                {/* FIXME: Automatically disabled during ESLint config update */}
                {/* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */}
                {(facility.fullAddress &&
                  facility.fullAddress.metropolitanStatisticalArea === "No MSA Present") ||
                facility.fullAddress?.manualMsa ? (
                  <SelectMetropolitanStatisticalArea
                    agentId={undefined}
                    facilityId={facility._id}
                    defaultMetropolitanStatisticalArea={
                      facility.fullAddress?.metropolitanStatisticalArea
                    }
                  />
                ) : (
                  <DisabledInputPopover
                    placeholder={facility.fullAddress?.metropolitanStatisticalArea}
                    inputName="MSA"
                    popover="MSAs are automatically assigned based on the Address. Edit the Address to re-calculate the MSA."
                  />
                )}
                {twoSpace}
                <DisabledInputPopover
                  placeholder={facility.pricingMsa}
                  inputName="Pricing MSA"
                  popover="Pricing MSA is a company internal identifier that informs the pricing decisions for shifts created by this workplace"
                />
              </Panel>

              <Panel header="Account Managers" key={FACILITY_DRAWER_SECTIONS.ACCOUNT_MANAGERS}>
                {!this.state.showPayrollInfo && (
                  <Button onClick={() => this.setState({ showPayrollInfoModal: true })}>
                    Show Payroll Info
                  </Button>
                )}
                {this.state.showPayrollInfo && (
                  <Fragment>
                    {twoSpace}
                    <Input
                      addonBefore="Payroll Name"
                      placeholder="Name"
                      defaultValue={facility.payroll.name}
                      onBlur={(v) => this.editPayroll(facility.payroll, "name", v.target.value)}
                    />
                    {twoSpace}
                    <Input
                      addonBefore="Payroll Email"
                      placeholder="Email"
                      defaultValue={facility.payroll.email}
                      onBlur={(v) => this.editPayroll(facility.payroll, "email", v.target.value)}
                    />
                  </Fragment>
                )}
                {twoSpace}
                <AccountManagerDisplay facility={facility} admins={this.state.admins} />
                {twoSpace}
                <BillingSpecialistDisplay facility={facility} admins={this.state.admins} />
                {twoSpace}
                {this.getCoordinators(user)}
              </Panel>

              <Panel
                header="Settings & Verification"
                key={FACILITY_DRAWER_SECTIONS.SETTINGS_VERIFICATION}
              >
                <div>
                  <CustomSettings userId={user._id} className="facility-drawer" />
                </div>
                {twoSpace}
                <>
                  <PreferenceHeading title="Verification Preference" />
                  <a
                    href={`${FACILITY_PREFERENCE_BASE_URL}?id=${facility.userId}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Click to open {facility.name} workplace's preference in new tool
                  </a>
                </>
                {twoSpace}
                {this.props.session?.adminProfile.roles?.includes("SHIFT_VERIFICATION") && (
                  <ReferenceTimeSheet userId={userId} />
                )}
              </Panel>

              <Panel header="Documents" key={FACILITY_DRAWER_SECTIONS.DOCUMENTS}>
                {userId && (
                  <Box sx={{ marginBottom: 2 }}>
                    <Ten99PolicyCoverageCard workplaceId={userId} />
                  </Box>
                )}
                <HcfRequirement hcfId={userId} />
              </Panel>

              <Panel
                header="Workplace Information"
                key={FACILITY_DRAWER_SECTIONS.WORKPLACE_INFORMATION}
              >
                <FacilityNotes facilityId={userId} />
                {twoSpace}
                <FacilityPhotos facility={facility} />
              </Panel>

              <Panel header="Billing & Payments" key={FACILITY_DRAWER_SECTIONS.BILLING_PAYMENTS}>
                <div>
                  <InstantPayDrawerCard facilityInfo={this.state.user} />
                </div>
                {twoSpace}
                <FacilityBilling
                  hcfId={userId}
                  hcfType={facility.type}
                  updateFacility={this.saveFacility}
                />
                {twoSpace}
                {(this.props.session?.adminProfile?.roles?.includes(EMPLOYEE_ROLES.INVOICE_EDIT) ||
                  this.props.session?.adminProfile?.roles?.includes(
                    EMPLOYEE_ROLES.ADMINISTRATIVE
                  )) && (
                  <FacilityPendingNotice
                    facilityId={facility.userId ?? ""}
                    tmz={facility.tmz ?? DEFAULT_TIMEZONE}
                  />
                )}
                {twoSpace}
                {(this.props.session?.adminProfile?.roles?.includes(EMPLOYEE_ROLES.INVOICE_EDIT) ||
                  this.props.session?.adminProfile?.roles?.includes(
                    EMPLOYEE_ROLES.ADMINISTRATIVE
                  )) && <FacilityInvalidContractWarning workplaceId={facility.userId ?? ""} />}
                {twoSpace}
                <Card>
                  {twoSpace}
                  <label>{"Pay Holiday Fee "}</label>&nbsp;&nbsp;
                  <Switch
                    key="holidayPay"
                    checked={facility.payOnHoliday}
                    onChange={this.handlePayOnHoliday}
                    loading={updatingPayOnHolidayCheck}
                  />
                  {twoSpace}
                  {facility.payOnHoliday && (
                    <div>
                      <label>{"Pay on all Holidays "}</label>&nbsp;&nbsp;
                      <Switch
                        key="payOnAllHolidays"
                        checked={facility.payOnAllHolidays}
                        onChange={this.handlePayOnAllHolidays}
                        loading={updatingPayOnAllHolidaysCheck}
                      />
                      {twoSpace}
                      <label>{"Or Pay on selected Holidays "}</label>
                      &nbsp;&nbsp;
                      <Select<string[]>
                        mode="multiple"
                        style={{ width: 300 }}
                        disabled={facility.payOnAllHolidays}
                        value={_.get(facility, "holidayList", [])}
                        onChange={this.handleHolidayListChange}
                        loading={updatingHolidayList}
                      >
                        {holidays.map((h) => (
                          <Option key={h.id} value={h.id}>
                            {h.name}
                          </Option>
                        ))}
                      </Select>
                      {twoSpace}
                      <b>
                        {/* FIXME: Automatically disabled during ESLint config update */}
                        {/* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */}
                        {facility.holidayFee &&
                          facility.holidayFee.type === CHARGE_RATE_MULTIPLIER &&
                          `Charge the workplace ${facility.holidayFee.increment
                            .toFixed(2)
                            .replace(/\.?0+$/, "")}x per hour on holidays `}
                        {/* FIXME: Automatically disabled during ESLint config update */}
                        {/* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */}
                        {facility.holidayFee &&
                          facility.holidayFee.type === FLAT &&
                          `Charge the workplace an extra $${facility.holidayFee.increment} per hour on holidays `}
                        {!facility.holidayFee && `No holiday rate configured `}
                        <EditTwoTone onClick={() => this.handleHolidayRate(facility.holidayFee)} />
                      </b>
                    </div>
                  )}
                  {facility.type && (
                    <QualificationHolidayFeeOverridesContainer
                      hcfId={userId}
                      hcfType={facility.type}
                      workplaceId={userId}
                      payOnHoliday={facility.payOnHoliday ?? false}
                    />
                  )}
                </Card>
                {twoSpace}
                <VariableChargeToggle
                  facility={facility}
                  adminProfile={this.props.session?.adminProfile}
                />
                {twoSpace}
                <RateNegotiationStatusCard
                  isAdmin={this.props.isAdmin}
                  facility={facility}
                  roles={this.props.session?.adminProfile.roles}
                  updateFacilityData={async ({ rateNegotiation }) => {
                    await this.saveFacility({
                      facilityId: facility.userId,
                      rateNegotiation,
                    });
                  }}
                />
              </Panel>

              <Panel
                header="Change of Ownership"
                key={FACILITY_DRAWER_SECTIONS.CHANGE_OF_OWNERSHIP}
              >
                {facility.userId && (
                  <Button
                    type="default"
                    onClick={() => this.setState({ showShiftMigrationModal: true })}
                  >
                    Migrate shifts
                  </Button>
                )}
              </Panel>

              <Panel header="Logs & History" key={FACILITY_DRAWER_SECTIONS.LOGS_HISTORY}>
                <LogContainer
                  type="FACILITY"
                  userId={userId}
                  currentUserId={currentUserID}
                  currentStage={undefined}
                />
                <FacilityReviewCard facilityId={facility.userId} />
                {twoSpace}
                <FacilityAppLinksCard user={user} />
                {twoSpace}
                {this.props.isAdmin && facility.userId && (
                  <ShiftBoardLinkButton workplaceId={facility.userId} />
                )}
                {twoSpace}
                {facility.userId && <WorkplaceAgreementSection workplaceId={facility.userId} />}
                {twoSpace}
                <StatusChangeLogsCard
                  logs={this.state.statusLogs}
                  tmz={facility.tmz}
                  type="Workplace"
                />
                {twoSpace}
                <ProfileChangeLogsCard id={facility.userId} />
              </Panel>
            </Collapse>
          </div>
        </Drawer>
        {this.state.showStatusModal && (
          <FacilityStatusModal
            status={this.state.selectedStatus}
            lastLog={_.isEmpty(this.state.statusLogs) ? {} : this.state.statusLogs[0]}
            userID={userId}
            saveFacilityStatus={this.saveFacilityStatus}
            currentUserID={currentUserID}
            visible={this.state.showStatusModal}
            onCancel={() => this.setState({ showStatusModal: false, selectedStatus: "" })}
            facility={facility}
            adminEmail={session?.adminProfile.email}
          />
        )}
        <Modal
          title="This feature is no longer supported"
          destroyOnClose={true}
          visible={this.state.addingCoordinator}
          okText="Show anyways"
          onOk={() => {
            this.setState({
              addingCoordinator: false,
              showCoordinatorsInfo: true,
            });
          }}
          onCancel={() => this.setState({ addingCoordinator: false })}
        >
          Please click on the Show Users button above to access this information
        </Modal>
        <Modal
          title="This feature is no longer supported"
          destroyOnClose={true}
          visible={this.state.showPayrollInfoModal}
          okText="Show anyways"
          onOk={() => {
            this.setState({
              showPayrollInfoModal: false,
              showPayrollInfo: true,
            });
          }}
          onCancel={() => this.setState({ showPayrollInfoModal: false })}
        >
          Please click on the Show Users button to access this information
        </Modal>
        <Modal
          title="Manage Users"
          destroyOnClose={true}
          visible={this.state.showUsersModal}
          footer={null}
          width="90%"
          onCancel={this.toggleUserModal}
          bodyStyle={{ height: "80vh", overflow: "scroll" }}
        >
          <TeamMembers inModal={true} />
        </Modal>
        <AutoLoginLinksModal
          visible={this.state.showAutoLoginLinksModal}
          onCancel={() => this.setState({ showAutoLoginLinksModal: false })}
          facilityId={userId}
        />
        <HolidayRateModal
          visible={this.state.showHolidayRate}
          onCancel={this.handleHolidayRate}
          holidayRateConfig={this.state.holidayRateConfig}
          onUpdateRateConfig={this.saveHolidayConfig}
          facility={facility}
        />
        {this.state.showShiftMigrationModal && facility.userId && facility.name && facility.tmz && (
          <ShiftMigrationDialog
            modalState={{
              modalStatus: ModalStatus.OPEN,
              modalIsOpen: true,
              modalIsClosed: false,
              setModalStatus: () => undefined,
              openModal: () => undefined,
              closeModal: () => this.setState({ showShiftMigrationModal: false }),
            }}
            origin={{
              id: facility.userId,
              name: facility.name,
              address: facility.fullAddress?.formatted,
              tmz: facility.tmz,
            }}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUserID: state.session.admin ? state.session.adminUserId : state.session.userId,
  session: state.session,
  locations: state.locations.locations,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      pseudo,
    },
    dispatch
  );

export default withLDConsumer()(connect(mapStateToProps, mapDispatchToProps)(FacilityDrawer));
